import { useTz } from '@entities/schedules';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import getDate from 'date-fns/getDate';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import parseISO from 'date-fns/parseISO';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import { useCallback, useEffect, useState } from 'react';

import { xhr } from '@shared/config';
import type { AvailablePeriod } from '@shared/types/available-periods';

export const useCalendarMode = () => {
	const [showCalendar, setShowCalendar] = useState(true);

	const toggleMode = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.stopPropagation();
		setShowCalendar((p) => !p);
	};

	return {
		showCalendar,
		toggleMode,
	};
};

export const useDate = (showCalendar: boolean) => {
	const [date, setDate] = useState(new Date());

	const changeDate = (date: Date) => setDate(date);

	const add = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.stopPropagation();
		if (showCalendar) setDate(addMonths(date, 1));
		if (!showCalendar) setDate(addDays(date, 1));
	};

	const subtract = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.stopPropagation();
		if (showCalendar) setDate(subMonths(date, 1));
		if (!showCalendar) setDate(subDays(date, 1));
	};

	return {
		date,
		add,
		subtract,
		changeDate,
	};
};

export const useMonth = (start: Date) => {
	const createMonthDates = useCallback(() => {
		const calendarStart = startOfWeek(startOfMonth(start), { weekStartsOn: 1 });
		const calendarEnd = endOfWeek(endOfMonth(start), { weekStartsOn: 1 });

		// получаем все даты месяца
		const month = [];
		let current = calendarStart;
		while (current < calendarEnd) {
			month.push(current);
			current = addDays(current, 1);
		}

		// разбиваем на недели
		const weeks = [];
		while (month.length > 0) {
			weeks.push(month.splice(0, 7));
		}

		return weeks;
	}, [start]);

	return {
		monthDates: createMonthDates(),
	};
};

export const useSessionLoader = (date: Date, coachId: number) => {
	const [available, setAvailable] = useState<any[]>([]);
	const [busy, setBusy] = useState<any[]>([]);
	const { tz } = useTz();

	const timeFormat = "yyyy-MM-dd'T'HH:mm";

	const loadSessions = () => {
		const controller = new AbortController();
		const load = async () => {
			try {
				const startTime = format(
					zonedTimeToUtc(
						date > new Date() && getMonth(date) > getMonth(new Date())
							? startOfMonth(date)
							: new Date(),
						tz
					),
					timeFormat
				);
				const endTime = format(zonedTimeToUtc(endOfMonth(date), tz), timeFormat);

				const requestParams = {
					start_time: startTime,
					end_time: endTime,
					coach: coachId,
				};

				const availableRequest = () =>
					xhr.get(`/api/v1/schedules/available_period/`, {
						params: { ...requestParams },
						signal: controller.signal,
					});

				const busyRequest = () =>
					xhr.get(`/api/v1/schedules/schedule/`, {
						params: { ...requestParams },
						signal: controller.signal,
					});

				const res = await Promise.all([availableRequest(), busyRequest()]);

				const available = res[0].data;
				const busy = res[1].data;
				const openAvailable = [];

				for (const session of available) {
					const findBusy = busy.find(
						(busySession: any) => busySession.start_time === session.start_time
					);

					if (!findBusy) openAvailable.push(session);
				}

				setAvailable(openAvailable);
				setBusy(res[1].data);
			} catch (error) {}
		};

		load();

		return () => {
			controller.abort();
		};
	};

	useEffect(loadSessions, [date, tz, coachId]);

	return { available, busy };
};

export const useDay = () => {
	const { tz } = useTz();

	const getSessionsInDay = (available: AvailablePeriod[], date: Date, mode: string) => {
		const sessionsInDay = available
			.filter((session) => {
				const sessionStartDate = utcToZonedTime(parseISO(session.start_time), tz);
				const selectedDate = utcToZonedTime(date, tz);
				const sessionInYear = getYear(sessionStartDate) === getYear(selectedDate);
				const sessionInMonth = getMonth(sessionStartDate) === getMonth(selectedDate);
				const sessionInDay = getDate(sessionStartDate) === getDate(selectedDate);
				const sessionHasRequiredMode =
					session.status.findIndex((status: string) => status === mode) !== -1;

				return sessionInDay && sessionInMonth && sessionInYear && sessionHasRequiredMode;
			})
			.sort((a, b) => {
				const aStart = parseISO(a.start_time);
				const bStart = parseISO(b.start_time);
				if (aStart > bStart) return 1;
				if (aStart < bStart) return -1;
				return 0;
			});

		return sessionsInDay;
	};
	return {
		getSessionsInDay,
	};
};
